import { useEffect, useState } from 'react';
import styled from '@emotion/styled';
import { css } from '@emotion/react';
import { Button, Field, TextInput, tokens } from '@hotjar/hotsauce';
import { useTranslation } from 'react-i18next';

import { useDebounce } from '@hotjar/webapp/hooks/useDebounce';
import { validateHTTPSUrl } from '@hotjar/insights-common/lib/url';
import { SiteIcon as SIcon } from '@hotjar/webapp/features/Activation/SiteIcon';

const MOBILE_BREAKPOINT = '(min-width: 960px)';

interface SiteUrlFormProps {
  className?: string;
  isLoading?: boolean;
  onSkip?: () => void;
  onSubmit: (url: string) => void;
}

const Form = styled.form`
  border-radius: 0;
  background: ${tokens.color.neutral_100};
  padding: 0 ${tokens.spacer.x2};

  @media ${MOBILE_BREAKPOINT} {
    padding: 0;
  }
`;

const StyledField = styled(Field)`
  width: 100%;

  label {
    text-transform: unset;
  }
`;

const InputWrapper = styled.div`
  background: ${tokens.color.neutral_100};
  position: relative;
`;

const SiteIcon = styled(SIcon)`
  position: absolute;
  top: 0;
  bottom: 0;
  left: 2px;
  z-index: 1;
  display: flex;
  align-items: center;
  pointer-events: none;
  width: ${tokens.spacer.x7};
`;

const ButtonWrapper = styled.div`
  margin-top: ${tokens.spacer.x3};
`;

const SkipButton = styled(Button)`
  margin-left: ${tokens.spacer.x2};
`;

export const SiteUrlForm = ({
  className,
  onSubmit,
  onSkip,
  isLoading,
}: SiteUrlFormProps): JSX.Element | null => {
  const [url, setUrl] = useState('');
  const [isInvalid, setIsInvalid] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const { t } = useTranslation();

  useEffect(() => {
    setIsInvalid(false);
    setErrorMessage('');
  }, [url]);
  useDebounce(() => setUrl(url), 500, [url]);

  const handleSubmit = (e: React.SyntheticEvent) => {
    e.preventDefault();
    if (!validateHTTPSUrl(url)) {
      setIsInvalid(true);
      setErrorMessage(t('activation:setup-wizard.site-https-url-error'));
    } else if (url.length > 250) {
      setIsInvalid(true);
      setErrorMessage(t('activation:setup-wizard.site-length-url-error'));
    } else {
      onSubmit(url);
    }
  };

  const handleUrlChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setUrl(e.target.value);
  };

  return (
    <Form className={className} onSubmit={handleSubmit} noValidate data-testid="site-url-form">
      <StyledField
        inputId="site_url"
        isInvalid={isInvalid}
        labelText={t('activation:setup-wizard.site-url-label')}
        helperText={t('activation:setup-wizard.site-url-helper')}
        invalidText={errorMessage}
      >
        <InputWrapper>
          <SiteIcon siteUrl={url} />
          <TextInput
            id="site_url"
            name="site_url"
            data-testid="site-url-input"
            autoFocus
            size="l"
            value={url}
            isInvalid={isInvalid}
            onChange={handleUrlChange}
            inputStyles={css`
              padding-left: ${tokens.spacer.x7};
            `}
            placeholder={t('activation:setup-wizard.site-https-url-placeholder')}
          />
        </InputWrapper>
      </StyledField>
      <ButtonWrapper>
        <Button
          kind="primary"
          type="submit"
          size="l"
          isLoading={isLoading}
          loadingText={t('common:registration.select-signup-method.submit-btn-loading')}
        >
          {t('activation:setup-wizard.continue')}
        </Button>
        {onSkip ? (
          <SkipButton kind="secondary" size="l" onClick={onSkip} data-testid="skip-site-url-button">
            {t('activation:setup-wizard.do-it-later')}
          </SkipButton>
        ) : null}
      </ButtonWrapper>
    </Form>
  );
};
